/**
 * Klient HTTP stajni — zamrożone endpointy §18.
 * PUT /api/layout {locationType:'stable'}, POST pet, POST visits.
 */
import {
  LayoutPutRequestSchema,
  LayoutPutResponseSchema,
  PetRequestSchema,
  PetResponseSchema,
  VisitsRequestSchema,
  VisitsResponseSchema,
  type LayoutPutResponse,
  type PetResponse,
  type PlacedBlock,
  type VisitsResponse,
} from '@pieskowo/shared';
import { SaveLocal } from '../../core/SaveLocal.js';
import {
  LOCAL_OWNED_BLOCKS_KEY,
  LOCAL_STABLE_LAYOUT_KEY,
  LOCAL_STABLE_VERSION_KEY,
  defaultOwnedStableBlocks,
  normalizeLayout,
} from './stableLayout.js';

function apiBase(): string {
  try {
    const v = import.meta.env?.VITE_API_URL;
    if (typeof v === 'string' && v.length > 0) return v.replace(/\/$/, '');
  } catch {
    /* test env */
  }
  return '';
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = SaveLocal.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export type PutLayoutResult =
  | { ok: true; version: number }
  | { ok: false; code: string; status: number };

export async function putStableLayout(
  layout: PlacedBlock[],
  version: number,
): Promise<PutLayoutResult> {
  const body = {
    locationType: 'stable' as const,
    layout,
    version,
  };
  const req = LayoutPutRequestSchema.safeParse(body);
  if (!req.success) return { ok: false, code: 'VALIDATION', status: 400 };

  try {
    const res = await fetch(`${apiBase()}/api/layout`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(req.data),
    });
    if (!res.ok) {
      let code = 'ERROR';
      try {
        const errJson = (await res.json()) as { error?: { code?: string } };
        code = errJson?.error?.code ?? code;
      } catch {
        /* ignore */
      }
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const parsed = LayoutPutResponseSchema.safeParse(json);
    if (!parsed.success) return { ok: false, code: 'VALIDATION', status: 500 };
    const out: LayoutPutResponse = parsed.data;
    saveLocalStable(layout, out.version);
    return { ok: true, version: out.version };
  } catch {
    return { ok: false, code: 'NETWORK', status: 0 };
  }
}

export function loadLocalStable(): { layout: PlacedBlock[]; layoutVersion: number } {
  const layout = normalizeLayout(
    SaveLocal.getJson<unknown>(LOCAL_STABLE_LAYOUT_KEY, []),
  );
  const layoutVersion = SaveLocal.getJson<number>(LOCAL_STABLE_VERSION_KEY, 0);
  return {
    layout,
    layoutVersion: Number.isInteger(layoutVersion) ? layoutVersion : 0,
  };
}

export function saveLocalStable(layout: PlacedBlock[], version: number): void {
  SaveLocal.setJson(LOCAL_STABLE_LAYOUT_KEY, layout);
  SaveLocal.setJson(LOCAL_STABLE_VERSION_KEY, version);
}

export function loadOwnedBlocks(): string[] {
  const raw = SaveLocal.getJson<unknown>(LOCAL_OWNED_BLOCKS_KEY, null);
  if (Array.isArray(raw) && raw.every((x) => typeof x === 'string') && raw.length > 0) {
    // Dołóż brakujące klocki stajenne do offline (dom mógł zapisać tylko meble)
    const list = raw as string[];
    const hasStable = list.some((id) =>
      ['fence_wood', 'hay_bale', 'horseshoe_deco', 'trough'].includes(id),
    );
    if (hasStable) return list;
    return [...list, ...defaultOwnedStableBlocks()];
  }
  return defaultOwnedStableBlocks();
}

export type StableLoadResult = {
  layout: PlacedBlock[];
  layoutVersion: number;
  source: 'local';
};

/** Brak GET layout stable w §18 — lustro lokalne (+ PUT przy zapisie). */
export function loadStableLayout(): StableLoadResult {
  const local = loadLocalStable();
  return {
    layout: local.layout,
    layoutVersion: local.layoutVersion,
    source: 'local',
  };
}

export type PetNpcResult =
  | { ok: true; data: PetResponse }
  | { ok: false; code: 'UNAUTHORIZED' | 'NETWORK' | 'ERROR'; status: number };

export async function postPetNpc(targetId: string): Promise<PetNpcResult> {
  const body = {
    targetType: 'npc' as const,
    targetId,
    idempotencyKey: newIdempotencyKey(),
  };
  const req = PetRequestSchema.safeParse(body);
  if (!req.success) return { ok: false, code: 'ERROR', status: 400 };
  try {
    const res = await fetch(`${apiBase()}/api/economy/pet`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(req.data),
    });
    if (!res.ok) {
      const code = res.status === 401 ? 'UNAUTHORIZED' : 'ERROR';
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const parsed = PetResponseSchema.safeParse(json);
    if (!parsed.success) return { ok: false, code: 'ERROR', status: 500 };
    return { ok: true, data: parsed.data };
  } catch {
    return { ok: false, code: 'NETWORK', status: 0 };
  }
}

export async function recordVisit(placeId: string): Promise<VisitsResponse | null> {
  const req = VisitsRequestSchema.safeParse({ placeId });
  if (!req.success) return null;
  try {
    const res = await fetch(`${apiBase()}/api/visits`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(req.data),
    });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const parsed = VisitsResponseSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
